'use client'

import { useState } from 'react'
import PropTypes from 'prop-types'
import { useLoadingStore } from '@repo/store/useLoadingStore'
import { Link } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import MUIStack from '@mui/material/Stack'
import MUIInput from '@mui/material/TextField'
import MUIButton from '@mui/material/Button'
import MUITypography from '@mui/material/Typography'
import { CheckIcon } from '@repo/ui/CheckIcon'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { AuthScreenTitle } from '@repo/ui/AuthScreenTitle'

const RequestPasswordReset = ({ request_DI }) => {
  const { requestPasswordReset } = useAuthenticationStore()
  const [userIdOrEmailAddress, setUserIdOrEmailAddress] = useState('')
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { isLoading } = useLoadingStore()
  const theme = useTheme()

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (isLoading) return

    try {
      setErrorMessage('')
      await requestPasswordReset({ userIdOrEmailAddress, makeApiCall: request_DI })
      setFormSubmitted(true)
    } catch (err) {
      console.log(err)
      setFormSubmitted(false)
      setErrorMessage(err?.response?.data?.errorMessage || 'An error occurred. Please try again.')
    }
  }

  return (
    <>
      <AuthScreenTitle
        title="Reset password"
        description={
          formSubmitted
            ? null
            : 'Enter your username or email address below and we’ll send you a link to reset your password'
        }
      />

      {formSubmitted ? (
        <MUIStack direction="row" alignItems="flex-start" justifyContent="flex-start" gap={4} sx={{ mt: -12 }}>
          <CheckIcon sx={{ width: '34px', height: '34px' }} />
          <MUITypography variant="body1">
            If an account exists that matches the details you submitted, we will email you a link to reset the password.
          </MUITypography>
        </MUIStack>
      ) : (
        <form
          onSubmit={handleSubmit}
          style={{
            width: '100%',
            position: 'relative',
            cursor: isLoading ? 'wait' : 'default'
          }}
          data-testid="loginForm"
        >
          <MUIStack direction="column" alignItems="center" justifyContent="center" gap={4}>
            <MUIInput
              data-testid="userIdOrEmailAddress"
              id="userIdOrEmailAddress"
              name="userIdOrEmailAddress"
              autoComplete="username"
              label="Username or email"
              type="text"
              sx={{ width: '100%' }}
              onChange={(e) => setUserIdOrEmailAddress(e.target.value)}
              required
              autoFocus
            />

            <MUIButton
              variant="contained"
              data-testid="loginBtn"
              type="submit"
              color="primary"
              size="small"
              sx={{ height: '56px', width: '100%' }}
              disabled={Boolean(isLoading)}
            >
              {isLoading ? 'Loading...' : 'Request password reset'}
            </MUIButton>

            <Link to="/auth/login" data-testid="loginLink" style={{ color: theme?.palette?.secondary?.dark || 'blue' }}>
              <MUITypography
                variant="body1"
                align="center"
                sx={{ width: '100%', color: 'secondary.main', cursor: 'pointer', mt: 0, mb: 4 }}
              >
                Back to login
              </MUITypography>
            </Link>

            <MUITypography
              variant="body1"
              align="center"
              data-testid="errorMessage"
              sx={{ width: '100%', color: 'error.main', position: 'absolute', bottom: '-50px' }}
            >
              {errorMessage}
            </MUITypography>
          </MUIStack>
        </form>
      )}
    </>
  )
}

export default RequestPasswordReset

RequestPasswordReset.propTypes = {
  request_DI: PropTypes.func
}
